import { logger } from "@/lib/logger";
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react';
import { isChunkLoadError, forceRefreshOnChunkError, getChunkErrorMessage, tryAutoRecoverFromChunkError } from '@/utils/lazyWithRetry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isChunkError: boolean;
  autoRecovering?: boolean;
}


/**
 * Detects "NotFoundError: Failed to execute 'insertBefore'/'removeChild' on 'Node'".
 * This is not an app logic bug: it happens when something outside React mutates
 * the DOM React owns — overwhelmingly browser auto-translate (Chrome/Edge) or a
 * browser extension rewriting text nodes. React then tries to commit against a
 * node that no longer lives where it expects and throws.
 * The only reliable recovery is a full remount (reload), once per session.
 */
function isExternalDomMutationError(error: Error | null): boolean {
  if (!error) return false;
  const msg = `${error.name}: ${error.message}`;
  return (
    /NotFoundError/i.test(msg) &&
    /(insertBefore|removeChild|appendChild)/i.test(msg)
  );
}

const DOM_RELOAD_FLAG = 'ptc_dom_mutation_reload';

export class ErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private maxAutoRetries = 1;


  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      isChunkError: false
    };
  }

  // NOTE: there is deliberately NO re-arm timer here. The DOM-mutation
  // auto-reload is strictly once per session. Re-arming it (e.g. after 15s)
  // means a persistently page-translating browser/extension keeps crashing
  // React after each reload, and each crash would trigger yet another full
  // reload — wiping unsaved form input every time. After the first
  // auto-recovery the user gets the manual fallback UI instead.



  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error)
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
      isChunkError: isChunkLoadError(error)
    });
    
    // Log error for monitoring (logger.error is NOT suppressed in production)
    logger.error('ErrorBoundary caught an error:', error?.message, error?.stack);
    
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Chunk-load failures are almost always a stale cached bundle / service
    // worker after a deploy. Recover automatically once per session instead of
    // stranding the user on a dead-end screen.
    if (isChunkLoadError(error)) {
      this.setState({ autoRecovering: tryAutoRecoverFromChunkError() });
      return;
    }

    // DOM mutated from outside React (browser translation / extensions).
    // A soft state reset cannot fix a corrupted DOM tree — reload once.
    if (isExternalDomMutationError(error)) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = sessionStorage.getItem(DOM_RELOAD_FLAG) === '1';
        if (!alreadyReloaded) {
          sessionStorage.setItem(DOM_RELOAD_FLAG, '1');
        }
      } catch { /* storage blocked — fall through to manual buttons */ }

      if (!alreadyReloaded) {
        this.setState({ autoRecovering: true });
        setTimeout(() => window.location.reload(), 300);
      }
      return;
    }


    // Auto-retry once for transient errors (e.g., auth state race conditions)
    if (this.retryCount < this.maxAutoRetries) {
      this.retryCount++;
      logger.error('ErrorBoundary: auto-retrying (attempt', this.retryCount, ')');
      setTimeout(() => {
        this.setState({
          hasError: false,
          error: null,
          errorInfo: null,
          isChunkError: false
        });
      }, 500);
    }
  }


  handleReset = () => {
    this.retryCount = 0;
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false
    });
  };

  handleForceRefresh = () => {
    forceRefreshOnChunkError();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Special handling for chunk-load errors
      if (this.state.isChunkError) {
        const errorInfo = getChunkErrorMessage(this.state.error!);
        
        return (
          <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="max-w-md w-full text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-amber-500/10 p-4">
                  <WifiOff className="h-8 w-8 text-amber-500" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h1 className="text-2xl font-semibold">
                  {this.state.autoRecovering ? 'Updating the app…' : errorInfo.title}
                </h1>
                <p className="text-muted-foreground">
                  {this.state.autoRecovering
                    ? 'A new version was released. Clearing the old files and reloading now.'
                    : errorInfo.description}
                </p>
              </div>


              <div className="space-y-3">
                <Button 
                  onClick={this.handleForceRefresh}
                  className="w-full flex items-center justify-center gap-2"
                  size="lg"
                >
                  <RefreshCw className="h-4 w-4" />
                  {errorInfo.action}
                </Button>
                
                <p className="text-xs text-muted-foreground">
                  If the problem persists, try clearing your browser cache or using a different network.
                </p>
              </div>
            </div>
          </div>
        );
      }

      // Default error UI for non-chunk errors
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <div className="rounded-full bg-destructive/10 p-4">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold">
                {isExternalDomMutationError(this.state.error) ? 'The page display got interrupted' : 'Something went wrong'}
              </h1>
              <p className="text-muted-foreground">
                {isExternalDomMutationError(this.state.error)
                  ? 'This usually happens when the browser tries to translate the page or an extension changes it. Turn off page translation for this site, then refresh.'
                  : 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'}
              </p>
            </div>


            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={this.handleReset}
                variant="outline" 
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
              <Button 
                onClick={() => window.location.reload()}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh Page
              </Button>
            </div>

            {this.state.error && (
              <details className="mt-8 text-left" open>
                <summary className="text-sm font-medium cursor-pointer mb-2">
                  Error details (copy these when reporting)
                </summary>

                <div className="bg-muted p-3 rounded text-xs font-mono overflow-auto max-h-40">
                  <div className="text-destructive font-semibold mb-2">
                    {this.state.error.name}: {this.state.error.message}
                  </div>
                  <pre className="whitespace-pre-wrap">
                    {this.state.error.stack}
                  </pre>
                  {this.state.errorInfo && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="font-semibold mb-2">Component Stack:</div>
                      <pre className="whitespace-pre-wrap text-muted-foreground">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    const payload = [
                      `${this.state.error?.name}: ${this.state.error?.message}`,
                      this.state.error?.stack ?? '',
                      this.state.errorInfo?.componentStack ?? '',
                    ].join('\n\n');
                    try { navigator.clipboard?.writeText(payload); } catch { /* ignore */ }
                  }}
                >
                  Copy error details
                </Button>
              </details>
            )}

          </div>
        </div>
      );
    }

    return this.props.children;
  }
}